import React, { useState, useContext, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { IconList, IconCard, LeafIcon as IconLeaf } from '../components/ui/Icons';
import LearningStatusBadge, { getLearningStatusMeta } from '../components/ui/LearningStatusBadge';
import FlashcardQuizV4 from '../components/quiz/FlashcardQuizV4';
import MultipleChoiceQuiz from '../components/quiz/MultipleChoiceQuiz';
import BlitzQuiz from '../components/quiz/BlitzQuiz';
import FamilyQuiz from '../components/quiz/FamilyQuiz';
import SpeedRecognitionQuiz from '../components/quiz/SpeedRecognitionQuiz';
import PflanzeInSchlauQuiz from '../components/quiz/PflanzeInSchlauQuiz';
import { ChevronDown, Trophy, RotateCcw, Play, CheckCircle, AlertCircle } from 'lucide-react';
import ReactConfetti from 'react-confetti';

const Quiz = ({
    plants,
    categories,
    userPlantData,
    onReview,
    trackInteraction,
    selectedPlantList,
    setSelectedPlantList,
    listStats,
    theme,
    fetchWithAuth,
    onUpdateUserPlantData,
    onPlantClick
}) => {
    // --- Suggest Learned Plants ---
    const [suggestedPlants, setSuggestedPlants] = useState([]);
    const [showSuggestModal, setShowSuggestModal] = useState(false);
    const [markingLearned, setMarkingLearned] = useState(false);

    const checkLearnedSuggestions = useCallback(async () => {
        if (!fetchWithAuth) return;
        try {
            const res = await fetchWithAuth('/api/plants/suggest-learned/');
            if (res.ok) {
                const data = await res.json();
                if (data.suggested_plants && data.suggested_plants.length > 0) {
                    setSuggestedPlants(data.suggested_plants);
                    setShowSuggestModal(true);
                }
            }
        } catch (e) {
            console.error('Error fetching learned suggestions:', e);
        }
    }, [fetchWithAuth]);

    const handleMarkAllLearned = async () => {
        if (!fetchWithAuth || markingLearned) return;
        setMarkingLearned(true);
        try {
            const plantIds = suggestedPlants.map(p => p.id);
            const res = await fetchWithAuth('/api/plants/mark-learned/', {
                method: 'POST',
                body: JSON.stringify({ plant_ids: plantIds }),
            });
            if (res.ok) {
                // Update local userPlantData for each plant
                plantIds.forEach(id => {
                    if (onUpdateUserPlantData) {
                        onUpdateUserPlantData(id, { learning_status: 'LEARNED' });
                    }
                });
                setShowSuggestModal(false);
                setSuggestedPlants([]);
            }
        } catch (e) {
            console.error('Error marking plants as learned:', e);
        } finally {
            setMarkingLearned(false);
        }
    };
    console.log("Quiz Component Rendering. Mode:", selectedPlantList);
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const location = useLocation();
    const [settings, setSettings] = useState({
        categories: [],
        status: ['NOT_LEARNED', 'IN_PROGRESS'],
        favoritesOnly: false,
        deckSize: 10,
    });
    const [quizMode, setQuizMode] = useState('selection');


    const [deck, setDeck] = useState([]);
    const [sessionResults, setSessionResults] = useState([]);

    const handleSettingChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            if (name === "favoritesOnly") {
                setSettings(prev => ({ ...prev, favoritesOnly: checked }));
            } else {
                setSettings(prev => ({
                    ...prev,
                    [name]: checked
                        ? [...prev[name], value]
                        : prev[name].filter(item => item !== value)
                }));
            }
        } else {
            setSettings(prev => ({ ...prev, [name]: (name === "deckSize" ? parseInt(value, 10) : value) }));
        }
    };

    const availablePlants = useMemo(() => {
        if (!selectedPlantList) return plants;
        return plants.filter(p => p.plant_lists && p.plant_lists.includes(selectedPlantList));
    }, [plants, selectedPlantList]);

    const availableCategories = useMemo(() => {
        const categoryIds = new Set(availablePlants.map(p => p.category));
        return categories.filter(c => categoryIds.has(c.id));
    }, [availablePlants, categories]);

    const startQuiz = (mode, plantSet = null) => {
        let filteredPlants = plantSet
            ? [...plantSet]
            : availablePlants.filter(p => {
                const user_data = userPlantData[p.id] || { learning_status: 'NOT_LEARNED', is_favorite: false };
                const categoryMatch = settings.categories.length === 0 || settings.categories.includes(String(p.category));
                const statusMatch = settings.status.length === 0 || settings.status.includes(user_data.learning_status);
                const favoriteMatch = user?.is_guest ? true : (!settings.favoritesOnly || user_data.is_favorite);
                return categoryMatch && statusMatch && favoriteMatch;
            });

        if (filteredPlants.length === 0) {
            alert("Keine Pflanzen für die ausgewählten Kriterien gefunden.");
            return;
        }

        const shuffled = [...filteredPlants].sort(() => 0.5 - Math.random());
        const finalDeck = shuffled.slice(0, settings.deckSize);

        setDeck(finalDeck);
        setSessionResults([]);
        setQuizMode(mode);

        if (trackInteraction) {
            trackInteraction('QUIZ_START', {
                mode: mode,
                deck_size: finalDeck.length,
                categories: settings.categories,
                status_filter: settings.status,
                favorites_only: settings.favoritesOnly
            });
        }
    };

    const isDue = (data) => {
        if (!data || !data.next_review_at) return true;
        const dueTime = new Date(data.next_review_at).getTime();
        return Date.now() >= dueTime;
    };

    const startTodayDeck = () => {
        const due = user?.is_guest
            ? availablePlants
            : availablePlants.filter(p => isDue(userPlantData[p.id]));
        const list = (due.length > 0 ? due : availablePlants);
        const shuffled = [...list].sort(() => 0.5 - Math.random());
        const finalDeck = shuffled.slice(0, settings.deckSize);
        setDeck(finalDeck);
        setSessionResults([]);
        setQuizMode('flashcard');

        if (trackInteraction) {
            trackInteraction('QUIZ_START', {
                mode: 'flashcard',
                deck_size: finalDeck.length,
                type: 'due_cards'
            });
        }
    };

    // Handle URL query parameters for direct mode access (moved down to avoid TDZ for availablePlants)
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const modeParam = searchParams.get('mode');
        if (modeParam && ['flashcard', 'multiple_choice', 'blitz', 'family', 'speed', 'pflanze_in_schlau', 'today'].includes(modeParam)) {
            if (quizMode === 'selection') {
                if (user?.is_guest && modeParam !== 'flashcard' && modeParam !== 'today') {
                    navigate('/auth?mode=register');
                    return;
                }
                if (modeParam === 'today') {
                    // Slight delay to ensure availablePlants are loaded
                    setTimeout(() => startTodayDeck(), 100);
                } else {
                    startQuiz(modeParam);
                }
            }
        }
    }, [location.search, availablePlants]);

    const handleFinish = (results) => {
        setSessionResults(results);
        setQuizMode('summary');

        if (trackInteraction) {
            const correctCount = results.filter(r => r.correct || r.knewIt).length;
            trackInteraction('QUIZ_COMPLETE', {
                mode: quizMode,
                total_cards: results.length,
                correct_cards: correctCount,
                accuracy: results.length > 0 ? (correctCount / results.length) : 0
            });
        }

        setTimeout(checkLearnedSuggestions, 3000);
    }

    const handleAbort = useCallback(() => {
        setDeck([]);
        setSessionResults([]);
        setQuizMode('selection');
    }, []);    const ModeCard = ({ title, description, icon, onClick, guestDisabled, isGuest, badge }) => (
        <button
            onClick={onClick}
            disabled={isGuest && guestDisabled}
            className={`group relative flex flex-row items-center text-left p-5 rounded-[2rem] border transition-all duration-500 gap-5 w-full overflow-hidden
                ${isGuest && guestDisabled
                    ? 'bg-gray-50/50 dark:bg-gray-900/30 border-gray-200 dark:border-gray-800 opacity-60 cursor-not-allowed'
                    : 'bg-white dark:bg-white/[0.03] border-gray-100 dark:border-white/5 hover:border-plantlix-500/50 hover:shadow-[0_20px_50px_rgba(39,145,106,0.12)] dark:hover:shadow-plantlix-900/10 hover:-translate-y-1'
                }
            `}
        >
            {/* Background Glow */}
            {!guestDisabled && (
                <div className="absolute -right-8 -bottom-8 w-24 h-24 bg-plantlix-500/5 rounded-full blur-2xl group-hover:bg-plantlix-500/10 transition-all duration-700" />
            )}
            
            <div className={`p-4 rounded-2xl transition-all duration-500 shrink-0 shadow-sm ${isGuest && guestDisabled ? 'bg-gray-100 dark:bg-gray-800 text-gray-400' : 'bg-gradient-to-br from-plantlix-50 to-white dark:from-plantlix-900/20 dark:to-transparent text-plantlix-600 dark:text-plantlix-400 group-hover:scale-110 group-hover:rotate-3'}`}>
                <span className="text-3xl block leading-none drop-shadow-sm">{icon}</span>
            </div>

            <div className="flex flex-col flex-1 py-1 min-w-0">
                <div className="flex items-center justify-between w-full mb-1.5">
                    <h3 className={`font-black text-lg italic tracking-tight ${isGuest && guestDisabled ? 'text-gray-500' : 'text-gray-900 dark:text-gray-100 group-hover:text-plantlix-600 dark:group-hover:text-plantlix-400 transition-colors'}`}>
                        {title}
                    </h3>
                    {badge && (
                        <span className="px-2 py-0.5 rounded-lg text-[9px] uppercase tracking-wider font-black bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200/50 dark:border-amber-500/20 shadow-sm">
                            {badge}
                        </span>
                    )}
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed font-medium line-clamp-2">
                    {description}
                </p>

                {isGuest && guestDisabled && (
                    <div className="mt-2.5 w-full flex items-center gap-2 text-[9px] font-black text-amber-600 dark:text-amber-500 uppercase tracking-[0.1em]">
                        <AlertCircle className="w-3.5 h-3.5" />
                        <span>Konto erforderlich</span>
                    </div>
                )}
            </div>

            {!guestDisabled && (
                <div className="shrink-0 text-gray-200 dark:text-gray-800 group-hover:text-plantlix-500 transition-all transform group-hover:translate-x-1">
                    <ChevronDown className="w-5 h-5 rotate-[-90deg]" />
                </div>
            )}
        </button>
    );

    if (quizMode === 'selection') {
        return (
            <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#070b14]' : 'bg-slate-50'} pb-20 relative overflow-hidden transition-colors duration-300`}>
                {/* Ambient Background Elements */}
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-plantlix-500/10 rounded-full blur-[120px] pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-plantlix-500/10 rounded-full blur-[100px] pointer-events-none" />

                {/* Hero Header */}
                <div className="relative pt-16 md:pt-24 pb-20 md:pb-32 px-4 md:px-6">
                    <div className="max-w-6xl mx-auto">
                        <div className="flex flex-col md:flex-row items-end justify-between gap-8 md:gap-10">
                            <div className="space-y-4 md:space-y-6 text-left">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-plantlix-500/10 border border-plantlix-500/20 rounded-xl animate-pulse">
                                    <IconLeaf className="w-3 h-3 text-plantlix-400" />
                                    <span className="text-[9px] font-black uppercase text-plantlix-400 tracking-[0.3em]">Plantlix Academy</span>
                                </div>
                                <h1 className={`text-3xl md:text-6xl font-black ${theme === 'dark' ? 'text-white' : 'text-slate-900'} tracking-tighter leading-[0.9] italic`}>
                                    Meistere die <br />
                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-plantlix-600 to-plantlix-400">
                                        Pflanzenwelt.
                                    </span>
                                </h1>
                                <p className={`text-sm md:text-xl ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'} max-w-xl font-medium leading-relaxed`}>
                                    Wähle dein Trainingsmodul und schärfe dein botanisches Auge mit spielerischen Herausforderungen.
                                </p>
                            </div>

                            {!user?.is_guest && (
                                <div className="hidden md:flex gap-6 animate-in slide-in-from-right duration-700">
                                    <div className={`${theme === 'dark' ? 'bg-white/5 border-white/10 shadow-2xl' : 'bg-white border-slate-200 shadow-xl'} backdrop-blur-xl p-6 rounded-[2rem] border flex flex-col items-center min-w-[150px]`}>
                                        <div className="text-[10px] text-plantlix-500 uppercase tracking-[0.4em] font-black mb-3">Elite Level</div>
                                        <div className={`text-5xl font-black ${theme === 'dark' ? 'text-white' : 'text-slate-900'} italic`}>
                                            {plants.filter(p => userPlantData[p.id]?.learning_status === 'LEARNED').length}
                                        </div>
                                        <div className={`text-[10px] ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'} uppercase tracking-widest font-black mt-2`}>Gelernt</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="max-w-6xl mx-auto px-6 mt-8 md:mt-12 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        {/* Settings Sidebar */}
                        <div className="lg:col-span-4 space-y-8">
                            <div className={`${theme === 'dark' ? 'bg-white/[0.03] border-white/5 shadow-2xl' : 'bg-white border-slate-200/60 shadow-xl'} backdrop-blur-3xl border rounded-[2.5rem] p-8 sticky top-10 transition-all duration-500 overflow-hidden`}>
                                {/* Decorative elements */}
                                <div className="absolute top-0 right-0 w-32 h-32 bg-plantlix-500/5 blur-3xl rounded-full -mr-16 -mt-16" />
                                
                                <div className="flex items-center gap-4 mb-10 relative z-10">
                                    <div className="p-3 bg-gradient-to-br from-plantlix-500 to-plantlix-600 shadow-[0_10px_20px_rgba(39,145,106,0.25)] text-white rounded-2xl animate-in zoom-in duration-500">
                                        <IconList className="w-6 h-6" />
                                    </div>
                                    <h2 className={`text-2xl font-black ${theme === 'dark' ? 'text-white' : 'text-slate-900'} italic uppercase tracking-tighter`}>Setup</h2>
                                </div>

                                <div className="space-y-10 relative z-10">
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="w-1.5 h-1.5 rounded-full bg-plantlix-500" />
                                            <label className={`text-[11px] font-black ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'} uppercase tracking-[0.4em]`}>Pflanzenliste</label>
                                        </div>
                                        <div className="relative group">
                                            <select
                                                value={selectedPlantList}
                                                onChange={(e) => {
                                                    setSelectedPlantList(e.target.value);
                                                    localStorage.setItem('selectedPlantList', e.target.value);
                                                    if (trackInteraction) trackInteraction('PLANT_LIST_CHANGE', { list: e.target.value, context: 'quiz' });
                                                }}
                                                className={`w-full appearance-none ${theme === 'dark' ? 'bg-white/5 border-white/10 text-white' : 'bg-slate-50 border-slate-200 text-slate-900'} border-2 rounded-2xl p-5 pr-12 focus:border-plantlix-500 focus:bg-white transition-all font-black text-xl cursor-pointer outline-none shadow-sm group-hover:border-plantlix-500/30`}
                                            >
                                                <option value="GALABAUER">GaLaBau {listStats ? `(${listStats['GALABAUER']})` : ''}</option>
                                                <option value="FRIEDHOFSGAERTNER">Friedhof {listStats ? `(${listStats['FRIEDHOFSGAERTNER']})` : ''}</option>
                                                <option value="ZIERPFLANZENGAERTNER">Zierpflanzen {listStats ? `(${listStats['ZIERPFLANZENGAERTNER']})` : ''}</option>
                                            </select>
                                            <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 group-hover:text-plantlix-500 transition-colors">
                                                <ChevronDown className="w-6 h-6" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="w-1.5 h-1.5 rounded-full bg-plantlix-500" />
                                            <label className={`text-[11px] font-black ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'} uppercase tracking-[0.4em]`}>Intensität</label>
                                        </div>
                                        <div className={`grid grid-cols-4 gap-2.5 ${theme === 'dark' ? 'bg-black/20 border-white/5' : 'bg-slate-50 border-slate-200/60'} p-2 rounded-2xl border transition-all duration-300`}>
                                            {[10, 20, 30, 50].map(size => (
                                                <button
                                                    key={size}
                                                    onClick={() => setSettings(s => ({ ...s, deckSize: size }))}
                                                    className={`py-4 rounded-xl text-xl font-black transition-all active:scale-90 ${settings.deckSize === size ? 'bg-plantlix-500 text-white shadow-xl shadow-plantlix-500/40 scale-105' : 'text-slate-400 hover:text-slate-600 hover:bg-white/5'}`}
                                                >
                                                    {size}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className={`pt-8 border-t ${theme === 'dark' ? 'border-white/10' : 'border-slate-100'}`}>
                                        <details className="group">
                                            <summary className={`flex items-center justify-between cursor-pointer list-none text-[11px] font-black ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'} uppercase tracking-[0.3em] hover:text-plantlix-500 transition-all`}>
                                                <span className="flex items-center gap-2">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-plantlix-500" />
                                                    Kategorien Filter
                                                </span>
                                                <ChevronDown className="w-5 h-5 transform group-open:rotate-180 transition-transform duration-300" />
                                            </summary>
                                            <div className={`mt-6 max-h-72 overflow-y-auto space-y-2 pr-2 scrollbar-thin ${theme === 'dark' ? 'scrollbar-thumb-white/10' : 'scrollbar-thumb-slate-200'} animate-in slide-in-from-top-4 duration-300`}>
                                                {availableCategories.map(cat => (
                                                    <label key={cat.id} className={`flex items-center gap-4 p-4 rounded-2xl ${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-slate-50'} cursor-pointer transition-all border border-transparent hover:border-plantlix-500/20`}>
                                                        <div className="relative flex items-center justify-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={settings.categories.includes(String(cat.id))}
                                                                onChange={handleSettingChange}
                                                                name="categories"
                                                                value={cat.id}
                                                                className="w-6 h-6 accent-plantlix-500 rounded-lg cursor-pointer"
                                                            />
                                                        </div>
                                                        <span className={`text-sm font-bold ${theme === 'dark' ? 'text-white/80' : 'text-slate-700'}`}>{cat.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </details>
                                    </div>
                                </div>
                            </div>
                        </div>

                            <div className="lg:col-span-8 space-y-8 md:space-y-12 pb-32">
                                {/* Premium Daily Banner */}
                                {!user?.is_guest && (
                                    <button
                                        onClick={startTodayDeck}
                                        className="w-full relative overflow-hidden p-8 md:p-10 rounded-[2.5rem] bg-gradient-to-br from-plantlix-600 via-plantlix-700 to-plantlix-900 text-white shadow-[0_30px_60px_-12px_rgba(39,145,106,0.3)] hover:shadow-[0_40px_80px_-12px_rgba(39,145,106,0.4)] hover:scale-[1.02] transition-all duration-500 text-left group border border-white/10 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-10"
                                    >
                                        {/* Dynamic Background Trophy */}
                                        <div className="absolute top-0 right-0 p-4 opacity-10 transform translate-x-12 -translate-y-12 group-hover:scale-110 group-hover:rotate-6 transition-all duration-1000 pointer-events-none">
                                            <Trophy className="w-56 h-56 md:w-72 md:h-72" strokeWidth={1} />
                                        </div>

                                        <div className="relative z-10 space-y-6 flex-1">
                                            <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-xl text-[10px] font-black uppercase tracking-[0.3em] border border-white/20 w-fit">
                                                <RotateCcw className="w-3.5 h-3.5 animate-spin-slow" />
                                                Active Routine
                                            </div>
                                            <div className="space-y-3">
                                                <h3 className="text-3xl md:text-5xl font-black italic tracking-tighter leading-none drop-shadow-md">TÄGLICHE SESSION</h3>
                                                <p className="text-plantlix-100/90 max-w-lg text-base md:text-lg font-medium leading-snug">
                                                    Individueller Lernplan basierend auf deinen <span className="text-white font-bold underline decoration-plantlix-400 decoration-2 underline-offset-4">Fortschritten</span>.
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="relative z-10 shrink-0 self-center md:self-auto w-full md:w-auto">
                                            <div className="w-full md:w-auto px-8 py-5 bg-white text-black rounded-2xl font-black text-base shadow-2xl flex items-center justify-center gap-3 group-hover:bg-plantlix-50 group-hover:scale-105 transition-all duration-300">
                                                <Play className="w-5 h-5 fill-current" />
                                                JETZT STARTEN
                                            </div>
                                        </div>
                                    </button>
                                )}

                            {/* Cards Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <ModeCard
                                    title="Lernkarten"
                                    description="Das Fundament. Lerne botanische Namen und Merkmale intensiv."
                                    icon="🎴"
                                    onClick={() => startQuiz('flashcard')}
                                    isGuest={user?.is_guest}
                                />
                                <ModeCard
                                    title="Bestimmungs-Quiz"
                                    description="Identifiziere Pflanzen anhand von High-Res Makroaufnahmen."
                                    icon="🎯"
                                    onClick={() => startQuiz('multiple_choice')}
                                    isGuest={user?.is_guest}
                                    guestDisabled={true}
                                />
                                <ModeCard
                                    title="Elite-Blitz"
                                    description="Extremer Zeitdruck. Wie viele Bestimmungen schaffst du?"
                                    icon="⚡"
                                    onClick={() => startQuiz('blitz')}
                                    isGuest={user?.is_guest}
                                    guestDisabled={true}
                                />
                                <ModeCard
                                    title="Taxonomie-Quiz"
                                    description="Ordne Pflanzen ihren biologischen Familien korrekt zu."
                                    icon="🧬"
                                    onClick={() => startQuiz('family')}
                                    isGuest={user?.is_guest}
                                    guestDisabled={true}
                                />
                                <ModeCard
                                    title="Speed-Battle"
                                    description="Wer hat die schnellste Reaktion? Multiplayer-Check."
                                    icon="🚀"
                                    onClick={() => startQuiz('speed')}
                                    isGuest={user?.is_guest}
                                    guestDisabled={true}
                                />
                                <ModeCard
                                    title="Botanik-Genie"
                                    description="Botanische Schreibweise perfektionieren. Tippe um die Wette."
                                    icon="🎓"
                                    onClick={() => startQuiz('pflanze_in_schlau')}
                                    isGuest={user?.is_guest}
                                    guestDisabled={true}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (quizMode === 'flashcard') {
        return <FlashcardQuizV4 deck={deck} onFinish={handleFinish} onReview={onReview} trackInteraction={trackInteraction} userPlantData={userPlantData} onAbort={handleAbort} theme={theme} />;
    }

    if (quizMode === 'multiple_choice') {
        return <MultipleChoiceQuiz deck={deck} onFinish={handleFinish} onReview={onReview} plants={plants} trackInteraction={trackInteraction} userPlantData={userPlantData} onAbort={handleAbort} theme={theme} />;
    }

    if (quizMode === 'blitz') {
        return <BlitzQuiz deck={deck} onFinish={handleFinish} onReview={onReview} trackInteraction={trackInteraction} userPlantData={userPlantData} onAbort={handleAbort} plants={plants} theme={theme} />;
    }

    if (quizMode === 'family') {
        return <FamilyQuiz deck={deck} onFinish={handleFinish} onReview={onReview} trackInteraction={trackInteraction} userPlantData={userPlantData} onAbort={handleAbort} plants={plants} theme={theme} />;
    }

    if (quizMode === 'speed') {
        return <SpeedRecognitionQuiz deck={deck} onFinish={handleFinish} onReview={onReview} trackInteraction={trackInteraction} userPlantData={userPlantData} onAbort={handleAbort} plants={plants} theme={theme} />;
    }

    if (quizMode === 'pflanze_in_schlau') {
        return <PflanzeInSchlauQuiz deck={deck} onFinish={handleFinish} onReview={onReview} trackInteraction={trackInteraction} userPlantData={userPlantData} onAbort={handleAbort} theme={theme} />;
    }

    if (quizMode === 'summary') {
        const knownCount = sessionResults.filter(r => {
            if (r.confidence) return r.confidence === 'good' || r.confidence === 'easy';
            return r.correct ?? r.knewIt;
        }).length;

        const unknownCards = sessionResults.filter(r => {
            if (r.confidence) return r.confidence === 'again' || r.confidence === 'hard';
            return !(r.correct ?? r.knewIt);
        }).map(r => r.plant);

        const uniqueUnknown = [...new Map(unknownCards.map(item => [item.id, item])).values()];
        const scorePercentage = Math.round((knownCount / deck.length) * 100);
        const radius = 60;
        const circumference = 2 * Math.PI * radius;
        const offset = circumference - (scorePercentage / 100) * circumference;

        return (
            <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#070b14]' : 'bg-white'} flex items-center justify-center p-4 md:p-12 relative overflow-hidden transition-colors duration-300`}>
                {/* Ambient Background Glows */}
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-plantlix-500/10 rounded-full blur-[120px] pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-plantlix-600/10 rounded-full blur-[120px] pointer-events-none" />

                {scorePercentage > 80 && <ReactConfetti recycle={false} numberOfPieces={300} gravity={0.2} style={{ zIndex: 100 }} />}

                <div className={`${theme === 'dark' ? 'bg-white/5 border-white/10 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.6)]' : 'bg-white border-slate-200 shadow-2xl'} backdrop-blur-3xl border rounded-[2.5rem] md:rounded-[3.5rem] p-8 md:p-16 text-center relative z-10 animate-in fade-in zoom-in duration-700`}>
                    
                    {/* Premium Progress Ring */}
                    <div className="relative flex items-center justify-center mb-10">
                        <svg className="w-40 h-40 md:w-56 md:h-56 transform -rotate-90">
                            <circle
                                cx="50%" cy="50%" r={radius}
                                className={`${theme === 'dark' ? 'stroke-white/5' : 'stroke-slate-100'} fill-none`}
                                strokeWidth="12"
                            />
                            <circle
                                cx="50%" cy="50%" r={radius}
                                className="stroke-plantlix-500 fill-none transition-all duration-1000 ease-out"
                                strokeWidth="12"
                                strokeDasharray={circumference}
                                style={{ strokeDashoffset: offset }}
                                strokeLinecap="round"
                            />
                        </svg>
                        <div className="absolute flex flex-col items-center">
                            <span className={`text-4xl md:text-6xl font-black ${theme === 'dark' ? 'text-white' : 'text-slate-900'} italic tracking-tighter tabular-nums drop-shadow-xl`}>
                                {scorePercentage}%
                            </span>
                            <span className="text-[10px] md:text-xs font-black uppercase tracking-[0.3em] text-plantlix-500 mt-1">Score</span>
                        </div>
                        
                        {scorePercentage === 100 && (
                            <div className="absolute -top-4 -right-2 p-3 bg-amber-400 text-amber-950 rounded-2xl shadow-[0_20px_40px_rgba(251,191,36,0.5)] animate-bounce border-2 border-white/20">
                                <Trophy className="w-6 h-6" />
                            </div>
                        )}
                    </div>

                    <h1 className={`text-3xl md:text-5xl font-black ${theme === 'dark' ? 'text-white' : 'text-slate-900'} mb-3 tracking-tighter leading-tight italic uppercase`}>
                        {scorePercentage === 100 ? 'MAKELFREI' : scorePercentage >= 80 ? 'EXZELLENT' : 'GUTE ARBEIT'}
                    </h1>
                    <p className={`${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'} text-sm md:text-lg mb-10 font-medium`}>
                        Du hast <span className={`${theme === 'dark' ? 'text-white' : 'text-plantlix-600'} font-bold`}>{knownCount}</span> von <span className={`${theme === 'dark' ? 'text-white' : 'text-plantlix-600'} font-bold`}>{deck.length}</span> Pflanzen gemeistert.
                    </p>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-4 md:gap-6 mb-10">
                        <div className="p-5 md:p-8 bg-plantlix-500/5 rounded-[2.2rem] border border-plantlix-500/10 flex flex-col items-center transition-transform hover:scale-105">
                            <div className="text-3xl md:text-5xl font-black text-plantlix-500 mb-1">{knownCount}</div>
                            <div className="text-[8px] md:text-[10px] font-black uppercase tracking-[0.4em] text-plantlix-500/60">RICHTIG</div>
                        </div>
                        <div className="p-5 md:p-8 bg-rose-500/5 rounded-[2.2rem] border border-rose-500/10 flex flex-col items-center transition-transform hover:scale-105">
                            <div className="text-3xl md:text-5xl font-black text-rose-500 mb-1">{deck.length - knownCount}</div>
                            <div className="text-[8px] md:text-[10px] font-black uppercase tracking-[0.4em] text-rose-500/60">REVIEW</div>
                        </div>
                    </div>

                    {uniqueUnknown.length > 0 && (
                        <div className={`${theme === 'dark' ? 'bg-black/30' : 'bg-slate-50'} rounded-[2.2rem] p-6 md:p-8 border border-white/5 text-left`}>
                            <h3 className={`font-black ${theme === 'dark' ? 'text-white/50' : 'text-slate-400'} uppercase tracking-[0.2em] text-[10px] mb-6 pl-2 flex items-center gap-3`}>
                                <RotateCcw className="w-4 h-4" />
                                Review-Liste
                            </h3>
                            <div className={`max-h-52 overflow-y-auto space-y-3 pr-2 scrollbar-thin ${theme === 'dark' ? 'scrollbar-thumb-white/10' : 'scrollbar-thumb-slate-200'}`}>
                                {uniqueUnknown.map(p => (
                                    <button 
                                        key={p.id} 
                                        onClick={() => onPlantClick?.(p)}
                                        className={`w-full p-4 ${theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-white border-slate-100'} rounded-2xl flex items-center justify-between group transition-all hover:bg-plantlix-500/5 hover:scale-[1.02] active:scale-95 cursor-pointer text-left`}
                                    >
                                        <div className="min-w-0 pr-4">
                                            <span className={`font-black ${theme === 'dark' ? 'text-white' : 'text-slate-900'} block italic text-lg leading-none truncate`}>{p.botanical_name}</span>
                                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 mt-1.5 block truncate">{p.german_name}</span>
                                        </div>
                                        <div className="w-10 h-10 rounded-full bg-plantlix-500/10 text-plantlix-500 flex items-center justify-center translate-x-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 transition-all">
                                            <Play className="w-4 h-4 fill-current ml-1" />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <button
                        onClick={() => setQuizMode('selection')}
                        className="w-full py-5 md:py-6 bg-gradient-to-r from-plantlix-500 to-plantlix-700 text-white rounded-[1.8rem] font-black text-xl shadow-[0_24px_48px_-12px_rgba(39,145,106,0.4)] transition-all hover:scale-[1.03] active:scale-95 flex items-center justify-center gap-4 group"
                    >
                        <RotateCcw className="w-6 h-6 group-hover:rotate-[-45deg] transition-transform" />
                        ÜBERSICHT
                    </button>
                </div>

                {/* --- 🌿 Suggest Learned Modal --- */}
                {showSuggestModal && suggestedPlants.length > 0 && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
                        <div className="bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-2xl max-w-md w-full p-8 border border-plantlix-100 dark:border-plantlix-900/30 text-center relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-plantlix-500 to-plantlix-500"></div>
                            
                            <div className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-full bg-plantlix-50 dark:bg-plantlix-900/20 text-plantlix-600 dark:text-plantlix-400">
                                <span className="text-4xl">🌿</span>
                            </div>

                            <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-3">
                                {suggestedPlants.length === 1 
                                    ? 'Du kennst diese Pflanze nun sehr gut!' 
                                    : 'Du kennst diese Pflanzen nun sehr gut!'}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-6">
                                {suggestedPlants.map(p => p.botanical_name || p.german_name).join(', ')}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
                                Möchtest du {suggestedPlants.length === 1 ? 'sie' : 'diese'} nun als <strong>Gelernt</strong> markieren?
                            </p>

                            <div className="space-y-3">
                                <button
                                    onClick={handleMarkAllLearned}
                                    disabled={markingLearned}
                                    className="w-full py-4 bg-plantlix-600 text-white rounded-xl hover:bg-plantlix-700 disabled:bg-plantlix-400 font-bold shadow-lg shadow-plantlix-600/20 transition-all flex items-center justify-center gap-2"
                                >
                                    {markingLearned ? (
                                        <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span> Wird gespeichert...</>
                                    ) : (
                                        <>✅ Ja, als gelernt markieren</>
                                    )}
                                </button>
                                <button
                                    onClick={() => setShowSuggestModal(false)}
                                    className="w-full py-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 font-medium transition-colors"
                                >
                                    Noch nicht
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return null;
};

export default Quiz;
